/**
 * Carbon Reporting API Client
 *
 * Interfaces with zeecore truth-based carbon emissions endpoints (/carbon).
 */

import { authenticatedFetch } from '../utils/apiClient';
import { ZEECORE_BASE_URL } from '../config/api';

const API_BASE_URL = ZEECORE_BASE_URL;

// ============================================================================
// Types
// ============================================================================

export interface VehicleCarbonEmissions {
  vehicle_entity_id: string;
  vehicle_registration?: string;
  date: string;
  emissions: {
    co2e_kg: number;
    scope_1_kg: number;
    scope_3_kg: number;
    breakdown: {
      fuel_combustion: number;
      well_to_tank: number;
    };
  };
  calculation: {
    tier: 1 | 2 | 3;
    method: string;
    emission_factors: {
      source: string;
      fuel_type: string;
      factor_kg_co2e_per_litre?: number;
      factor_kg_co2e_per_km?: number;
    };
  };
  confidence: {
    score: number;
    grade: 'A' | 'B' | 'C' | 'D' | 'F';
    data_quality: {
      fuel_data: 'actual' | 'estimated' | 'missing';
      distance_data: 'gps' | 'declared' | 'estimated' | 'missing';
      vehicle_spec: 'complete' | 'partial' | 'missing';
      load_data: 'actual' | 'estimated' | 'missing';
    };
  };
  data_sources: Array<{
    type: 'fuel' | 'distance' | 'vehicle_spec' | 'load';
    source: string;
    confidence: number;
  }>;
  timestamp: string;
}

export interface FleetCarbonSummary {
  date_range: {
    from: string;
    to: string;
  };
  fleet_totals: {
    total_co2e_kg: number;
    total_scope_1_kg: number;
    total_scope_3_kg: number;
    vehicle_count: number;
    days_with_data: number;
  };
  by_vehicle: Array<{
    vehicle_entity_id: string;
    vehicle_registration?: string;
    total_co2e_kg: number;
    days_active: number;
    avg_daily_emissions: number;
    confidence_avg: number;
  }>;
  by_date: Array<{
    date: string;
    total_co2e_kg: number;
    vehicle_count: number;
    avg_confidence: number;
  }>;
  by_tier: {
    tier_1_vehicles: number;
    tier_2_vehicles: number;
    tier_3_vehicles: number;
  };
  calculation: {
    avg_tier: number;
    avg_confidence: number;
    data_quality_grade: string;
  };
  timestamp: string;
}

export interface TenantCarbonStatus {
  tenant_id: string;
  configured_adapters: {
    fuel?: string;
    distance?: string;
    vehicle?: string;
    load?: string;
  };
  expected_tier: 1 | 2 | 3;
  missing_for_improvement: string[];
  current_capabilities: string[];
}

export interface EmissionFactorSet {
  source: string;
  year: number;
  factors: Array<{
    fuel_type: string;
    kg_co2e_per_litre?: number;
    kg_co2e_per_kwh?: number;
    scope_1?: number;
    scope_3?: number;
  }>;
}

export interface Scope3Export {
  reporting_entity: string;
  reporting_period: string;
  scope3_category: string;
  activity_data: {
    total_distance_km: number;
    total_fuel_litres: number;
    vehicle_count: number;
  };
  emission_summary: {
    total_emissions_tco2e: number;
    by_vehicle_type: Record<string, number>;
  };
  calculation_methodology: string;
  data_quality_score: string;
  third_party_verified: boolean;
  audit_trail_available: boolean;
}

export interface BatchCarbonRequest {
  tenant_id: string;
  requests: Array<{
    vehicle_entity_id: string;
    date: string;
  }>;
}

export interface BatchCarbonResult {
  total: number;
  successful: number;
  failed: number;
  results: Array<{
    vehicle_entity_id: string;
    date: string;
    result?: VehicleCarbonEmissions;
    error?: string;
  }>;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Calculate carbon emissions for a single vehicle on a specific date
 */
export async function calculateVehicleCarbon(
  vehicleEntityId: string,
  date: string,
  tenantId: string = 'default'
): Promise<VehicleCarbonEmissions> {
  const response = await authenticatedFetch(`${API_BASE_URL}/carbon/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      vehicle_entity_id: vehicleEntityId,
      date,
      tenant_id: tenantId,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to calculate carbon: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get carbon emissions for a vehicle on a specific date (GET endpoint)
 */
export async function getVehicleCarbon(
  vehicleEntityId: string,
  date: string,
  tenantId: string = 'default'
): Promise<VehicleCarbonEmissions> {
  const params = new URLSearchParams({ date, tenant_id: tenantId });
  
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/vehicle/${encodeURIComponent(vehicleEntityId)}?${params.toString()}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to get vehicle carbon: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get fleet carbon summary for a date range or period
 */
export async function getFleetCarbonSummary(
  tenantId: string,
  options: {
    period?: string; // YYYY-MM or YYYY
    from?: string;   // YYYY-MM-DD
    to?: string;     // YYYY-MM-DD
  }
): Promise<FleetCarbonSummary> {
  const params = new URLSearchParams({ tenant_id: tenantId });
  
  if (options.period) {
    params.set('period', options.period);
  } else if (options.from && options.to) {
    params.set('from', options.from);
    params.set('to', options.to);
  } else {
    throw new Error('Either period or from/to dates must be provided');
  }
  
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/summary?${params.toString()}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to get fleet carbon summary: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get tenant carbon reporting configuration status
 */
export async function getTenantCarbonStatus(
  tenantId: string
): Promise<TenantCarbonStatus> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/status/${encodeURIComponent(tenantId)}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to get tenant status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Update tenant carbon reporting configuration
 */
export async function updateTenantCarbonConfig(
  tenantId: string,
  config: {
    adapters: {
      fuel?: string;
      distance?: string;
      vehicle?: string;
      load?: string;
    };
  }
): Promise<TenantCarbonStatus> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/config/${encodeURIComponent(tenantId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to update tenant config: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get available emission factor sets
 */
export async function getEmissionFactors(): Promise<{
  available_sources: string[];
  default_source: string;
}> {
  const response = await authenticatedFetch(`${API_BASE_URL}/carbon/factors`);
  
  if (!response.ok) {
    throw new Error(`Failed to get emission factors: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get specific emission factor set details
 */
export async function getEmissionFactorSet(
  source: string,
  year?: number
): Promise<EmissionFactorSet> {
  const params = year ? new URLSearchParams({ year: year.toString() }) : '';
  
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/factors/${encodeURIComponent(source)}${params ? `?${params}` : ''}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to get emission factor set: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Generate Scope 3 carbon export for reporting
 */
export async function getScope3Export(
  tenantId: string,
  reportingPeriod: string, // YYYY format
  category: 'upstream' | 'downstream' = 'upstream'
): Promise<Scope3Export> {
  const params = new URLSearchParams({
    tenant_id: tenantId,
    reporting_period: reportingPeriod,
    category,
  });
  
  const response = await authenticatedFetch(
    `${API_BASE_URL}/carbon/export/scope3?${params.toString()}`
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to generate Scope 3 export: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Calculate carbon for multiple vehicles/dates in one request
 */
export async function batchCalculateCarbon(
  request: BatchCarbonRequest
): Promise<BatchCarbonResult> {
  const response = await authenticatedFetch(`${API_BASE_URL}/carbon/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || `Failed to batch calculate carbon: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Get carbon data for multiple vehicles in a date range
 */
export async function getBatchVehicleCarbon(
  vehicleEntityIds: string[],
  startDate: string,
  endDate: string,
  tenantId: string = 'default'
): Promise<VehicleCarbonEmissions[]> {
  const dates = getDatesInRange(startDate, endDate);
  
  const requests = vehicleEntityIds.flatMap(vehicle_entity_id =>
    dates.map(date => ({ vehicle_entity_id, date }))
  );
  
  // Split into batches of 50 to avoid overwhelming the server
  const batchSize = 50;
  const batches: BatchCarbonRequest[] = [];
  
  for (let i = 0; i < requests.length; i += batchSize) {
    batches.push({
      tenant_id: tenantId,
      requests: requests.slice(i, i + batchSize),
    });
  }
  
  const results = await Promise.all(
    batches.map(batch => batchCalculateCarbon(batch))
  );
  
  // Filter out errors and extract successful results
  const successfulResults: VehicleCarbonEmissions[] = [];
  results.forEach(batchResult => {
    batchResult.results.forEach(item => {
      if (item.result) {
        successfulResults.push(item.result);
      }
    });
  });
  
  return successfulResults;
}

/**
 * Health check for carbon reporting service
 */
export async function carbonHealthCheck(): Promise<{
  status: 'healthy' | 'unhealthy';
  emission_factors_loaded?: number;
  error?: string;
  timestamp: string;
}> {
  const response = await authenticatedFetch(`${API_BASE_URL}/carbon/health`);
  
  if (!response.ok) {
    throw new Error(`Carbon service health check failed: ${response.status}`);
  }
  
  return response.json();
}

// ============================================================================
// Helper Functions
// ============================================================================

function getDatesInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}
